// Platform embed resolution — pure, dependency-free, so both VideoPlayer.jsx
// and docFormat.jsx can ask "is this playable in-app?" without an import cycle
// (VideoPlayer needs sourceLink from docFormat).
//
// Per platform:
//   YouTube  /embed/<id>?start=s
//   Rutube   /play/embed/<id>/?t=s
//   TikTok   /player/v1/<id>?timestamp=s   (official embed player, portrait)
// Sources with no embeddable player (local/drive uploads, audio_url, rss)
// return null and callers fall back to the external link.

// Which platform a provenance payload belongs to — origin label first, URL
// shape as the fallback. null = no in-app player.
export function embedPlatform(meta) {
  const url = meta?.source_url || ''
  const origin = String(meta?.origin || '').toLowerCase()
  if (origin.startsWith('youtube') || /youtube\.com|youtu\.be/.test(url)) return 'youtube'
  if (origin === 'rutube' || /rutube\.ru/.test(url)) return 'rutube'
  if (origin === 'tiktok' || /tiktok\.com/.test(url)) return 'tiktok'
  return null
}

// Vertical platforms need a portrait frame — a 9:16 clip letterboxed into a
// 16:9 box is mostly black bars.
export function embedShape(meta) {
  return embedPlatform(meta) === 'tiktok' ? 'portrait' : 'landscape'
}

export function videoIdOf(meta) {
  if (meta?.video_id) return String(meta.video_id)
  const url = meta?.source_url || ''
  switch (embedPlatform(meta)) {
    case 'youtube':
      return (url.match(/[?&]v=([\w-]{6,})/) || url.match(/shorts\/([\w-]{6,})/) || url.match(/youtu\.be\/([\w-]{6,})/) || [])[1] || null
    case 'rutube':
      return (url.match(/video\/([0-9a-f]{16,})/) || [])[1] || null
    case 'tiktok':
      return (url.match(/\/(?:video|photo)\/(\d{6,})/) || [])[1] || null
    default:
      return null
  }
}

// Embed URL for a provenance payload, or null when the platform has none.
//
// NOTE on `seconds`: YouTube and Rutube seek from the URL. TikTok does NOT —
// its `timestamp` param is a 0/1 toggle for the time DISPLAY, not a position.
// Seeking (and unmuting) a TikTok embed goes through the player's postMessage
// API instead; see tiktokCommand + VideoPlayerModal.
export function embedUrl(meta, seconds) {
  const t = Math.max(0, Math.floor(seconds || 0))
  const id = videoIdOf(meta)
  if (!id) return null
  switch (embedPlatform(meta)) {
    case 'youtube':
      return `https://www.youtube.com/embed/${id}?start=${t}&autoplay=1&rel=0`
    case 'rutube':
      return `https://rutube.ru/play/embed/${id}/?t=${t}`
    case 'tiktok':
      // muted=0 asks for sound; the browser can still force a muted autoplay,
      // which is why the modal also sends `unMute` once the player is ready.
      return `https://www.tiktok.com/player/v1/${id}?autoplay=1&muted=0&controls=1&music_info=0&description=0&rel=0`
    default:
      return null
  }
}

// One command to a TikTok embed player. Documented commands: play, pause,
// seekTo (seconds), mute, unMute. There is deliberately no volume-level
// command in TikTok's API — only mute/unMute, plus the player's own on-screen
// volume slider (volume_control, on by default).
export function tiktokCommand(frameWindow, type, value) {
  if (!frameWindow) return
  frameWindow.postMessage({ type, value, 'x-tiktok-player': true }, '*')
}

// Convenience: does this provenance support the in-app player?
export const canEmbed = (meta) => !!embedUrl(meta, 0)
